import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser } from '../../../../store/user/user.selector';
import { updateUserTokens } from '../../../../store/user/user.action';
import { checkChecklistUnlockStatus, getChecklistImageUrl, getAvailableSeries } from '../../../../utils/firebase/firebase.utils';
import { addPendingGroupCardsForSeries, countPendingGroupCardsForSeries } from '../../../../utils/groups/importGroupCode';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { fadeInUp } from '../../../../animations/Animations.js';
import { motion } from 'framer-motion';

// components
import LoadingIcon from '../../../../components/loading-icon/LoadingIcon';

// styles
import {
    ChecklistPanelContainer,
    MonthHeader,
    MonthGrid,
    ChecklistCard,
    ChecklistCardContent,
    ChecklistImage,
    ChecklistTitle,
    ChecklistDetails,
    UnlockButton,
    LockIcon,
    OpenButton
} from './ChecklistPanel.styles.js';

// assets
import lockIcon from '../../../../assets/system/icons/lock-icon.svg';

export default function ChecklistPanel({ data, basePath, showUnlockedOnly = false, sport, year, manufacturer, viewingUserID = null }) {
    const dispatch = useDispatch();
    const user = useSelector(selectUser);
    const [unlockStatuses, setUnlockStatuses] = useState({});
    const [unlockingSeries, setUnlockingSeries] = useState(null);
    const [imageLoadStates, setImageLoadStates] = useState({});
    const startedImageLoads = useRef(new Set());

    // Use viewingUserID if provided, otherwise use current user's ID
    const targetUserID = viewingUserID || user?.uid;

    // Check unlock status for each checklist
    useEffect(() => {
        const checkUnlockStatuses = async () => {
            const statuses = {};

            if (!targetUserID || !data) {
                setUnlockStatuses({});
                return;
            }

            for (const seriesKey of Object.keys(data)) {
                const unlockStatus = await checkChecklistUnlockStatus(
                    targetUserID,
                    sport,
                    year,
                    manufacturer,
                    seriesKey
                );
                statuses[seriesKey] = unlockStatus;
            }

            setUnlockStatuses(statuses);
        };

        if (targetUserID && data) {
            checkUnlockStatuses();
        }
    }, [targetUserID, data, sport, year, manufacturer]);

    // Fetch collected series data for unlocked checklists
    useEffect(() => {
        const fetchCollectedSeriesData = async () => {
            const collectedData = {};

            for (const seriesKey of Object.keys(data)) {
                const isUnlocked = unlockStatuses[seriesKey]?.isUnlocked;
                if (isUnlocked) {
                    try {
                        const seriesData = await getAvailableSeries(targetUserID, sport, year, manufacturer);
                        // seriesData is an object where keys are series names
                        if (seriesData && seriesData[seriesKey]) {
                            collectedData[seriesKey] = seriesData[seriesKey];
                        }
                    } catch (error) {
                        // Error fetching collected data - skip this series
                    }
                }
            }
        };

        if (targetUserID && data && Object.keys(unlockStatuses).length > 0) {
            fetchCollectedSeriesData();
        }
    }, [targetUserID, data, sport, year, manufacturer, unlockStatuses]);

    const handleUnlockClick = async (e, seriesKey) => {
        e.stopPropagation();

        // Don't allow unlocking when viewing a friend's collection
        if (viewingUserID) {
            return;
        }

        setUnlockingSeries(seriesKey);

        try {
            const functions = getFunctions();
            const unlockChecklistFunction = httpsCallable(functions, 'unlockChecklist');

            const result = await unlockChecklistFunction({
                sport,
                year,
                manufacturer,
                series: seriesKey
            });

            const { success, tokensDeducted } = result.data;

            if (success) {
                dispatch(updateUserTokens(-tokensDeducted));
                const newStatus = await checkChecklistUnlockStatus(user.uid, sport, year, manufacturer, seriesKey);
                setUnlockStatuses(prev => ({
                    ...prev,
                    [seriesKey]: newStatus
                }));

                const pendingCount = await countPendingGroupCardsForSeries(
                    user.uid, sport, year, manufacturer, seriesKey
                );
                if (pendingCount > 0) {
                    const shouldAdd = window.confirm(
                        `Add ${pendingCount} ${pendingCount === 1 ? 'card' : 'cards'} from a previous code to this checklist?`
                    );
                    if (shouldAdd) {
                        await dispatch(addPendingGroupCardsForSeries(
                            user.uid, sport, year, manufacturer, seriesKey
                        ));
                    }
                }
            }
        } catch (error) {
            if (error.code === 'functions/failed-precondition') {
                alert('Insufficient tokens to unlock this checklist');
            } else if (error.code === 'functions/already-exists') {
                alert('This checklist is already unlocked');
            } else {
                alert('Failed to unlock checklist. Please try again.');
            }
        } finally {
            setUnlockingSeries(null);
        }
    };

    const handleImageLoad = useCallback((seriesKey) => {
        setImageLoadStates(prev => ({
            ...prev,
            [seriesKey]: { loaded: true, error: false }
        }));
    }, []);

    const handleImageError = useCallback((seriesKey) => {
        setImageLoadStates(prev => ({
            ...prev,
            [seriesKey]: { loaded: false, error: true }
        }));
    }, []);

    // Preload images when component mounts
    useEffect(() => {
        if (data && Object.keys(data).length > 0) {
            // Reset the ref when data/sport/year/manufacturer changes
            startedImageLoads.current.clear();

            Object.keys(data).forEach(seriesKey => {
                // Only start loading if we haven't already started loading this image
                if (!startedImageLoads.current.has(seriesKey)) {
                    startedImageLoads.current.add(seriesKey);
                    const img = new Image();
                    img.onload = () => handleImageLoad(seriesKey);
                    img.onerror = () => handleImageError(seriesKey);
                    img.src = getChecklistImageUrl(sport, year, manufacturer, seriesKey);
                }
            });
        }
    }, [data, sport, year, manufacturer, handleImageLoad, handleImageError]);

    // Filter checklists based on showUnlockedOnly
    const filteredData = showUnlockedOnly
        ? Object.keys(data).filter(seriesKey => unlockStatuses[seriesKey]?.isUnlocked)
        : Object.keys(data);

    // Sort and group by release month (most recent at top)
    const seriesByMonth = useMemo(() => {
        const getMonthInfo = (seriesData) => {
            let date = null;
            const timestamp = seriesData?.releaseDate;
            if (timestamp) {
                if (typeof timestamp.toDate === 'function') {
                    date = timestamp.toDate();
                } else if (timestamp.seconds !== undefined) {
                    date = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
                }
            }
            if (!date) {
                const releaseMonth = seriesData?.releaseMonth;
                if (releaseMonth !== undefined && releaseMonth !== null) {
                    const yearNum = parseInt(year, 10) || new Date().getFullYear();
                    if (typeof releaseMonth === 'number' && releaseMonth >= 1 && releaseMonth <= 12) {
                        date = new Date(yearNum, releaseMonth - 1, 1);
                    } else if (typeof releaseMonth === 'string') {
                        const match = releaseMonth.match(/^(\d{4})-(\d{1,2})/);
                        if (match) {
                            date = new Date(parseInt(match[1], 10), parseInt(match[2], 10) - 1, 1);
                        }
                    }
                }
            }
            if (!date) {
                return { key: '__unknown__', sortValue: 0, label: 'Other' };
            }
            const y = date.getFullYear();
            const m = date.getMonth();
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
            return {
                key: `${y}-${String(m + 1).padStart(2, '0')}`,
                sortValue: date.getTime(),
                label: `${monthNames[m]} ${y}`
            };
        };

        const sorted = [...filteredData].sort((a, b) => {
            const infoA = getMonthInfo(data[a]);
            const infoB = getMonthInfo(data[b]);
            if (infoB.sortValue !== infoA.sortValue) return infoB.sortValue - infoA.sortValue;
            return (a || '').localeCompare(b || '');
        });
        const groups = new Map();
        sorted.forEach((seriesKey) => {
            const info = getMonthInfo(data[seriesKey]);
            if (!groups.has(info.key)) {
                groups.set(info.key, { label: info.label, sortValue: info.sortValue, series: [] });
            }
            groups.get(info.key).series.push(seriesKey);
        });
        return Array.from(groups.entries())
            .sort(([, a], [, b]) => b.sortValue - a.sortValue)
            .map(([key, { label, series }]) => ({ key, label, series }));
    }, [filteredData, data, year]);

    const allSeriesInOrder = useMemo(() =>
        seriesByMonth.flatMap(({ label, series }) =>
            series.map((seriesKey, i) => ({ seriesKey, label: i === 0 ? label : null }))
        ),
        [seriesByMonth]
    );

    return (
        <ChecklistPanelContainer>
            <MonthGrid>
                {allSeriesInOrder.map(({ seriesKey, label }, index) => (
                    <motion.div
                        key={seriesKey}
                        initial="hidden"
                        animate="visible"
                        variants={fadeInUp}
                        custom={index * 0.025}
                        style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                    >
                        {label && <MonthHeader>{label}</MonthHeader>}
                        <ChecklistCard
                            as={unlockStatuses[seriesKey]?.isUnlocked ? Link : 'div'}
                            to={unlockStatuses[seriesKey]?.isUnlocked ? `${basePath}/${seriesKey}` : undefined}
                            $isUnlocked={unlockStatuses[seriesKey]?.isUnlocked}
                        >
                            <ChecklistCardContent>
                                <ChecklistImage
                                    $hasImage={imageLoadStates[seriesKey]?.loaded && !imageLoadStates[seriesKey]?.error}
                                    $isLocked={!unlockStatuses[seriesKey]?.isUnlocked}
                                >
                                    {(!imageLoadStates[seriesKey]?.loaded && !imageLoadStates[seriesKey]?.error) && (
                                        <LoadingIcon />
                                    )}
                                    {imageLoadStates[seriesKey]?.loaded && (
                                        <img
                                            src={getChecklistImageUrl(sport, year, manufacturer, seriesKey)}
                                            alt={`${seriesKey} checklist`}
                                            style={{ display: 'block', opacity: 1 }}
                                        />
                                    )}
                                </ChecklistImage>

                                <ChecklistTitle>
                                    <h3>{seriesKey}</h3>
                                </ChecklistTitle>

                                <ChecklistDetails>
                                    {!unlockStatuses[seriesKey]?.isUnlocked && !viewingUserID && (
                                        <UnlockButton
                                            className="panel-bg"
                                            onClick={(e) => handleUnlockClick(e, seriesKey)}
                                            disabled={unlockingSeries === seriesKey}
                                        >
                                            {unlockingSeries === seriesKey && <LoadingIcon size="small" />}
                                            <>
                                                <h4>
                                                    {unlockingSeries === seriesKey ? 'UNLOCKING...' : 'UNLOCK'}
                                                </h4>
                                                {!unlockStatuses[seriesKey]?.isUnlocked && <LockIcon src={lockIcon} alt="locked" onError={(e) => e.target.style.display = 'none'} />}
                                            </>
                                        </UnlockButton>
                                    )}
                                    {unlockStatuses[seriesKey]?.isUnlocked && (
                                        <OpenButton className="panel-bg">
                                            <h4>OPEN</h4>
                                        </OpenButton>
                                    )}
                                </ChecklistDetails>
                            </ChecklistCardContent>
                        </ChecklistCard>
                    </motion.div>
                ))}
            </MonthGrid>
        </ChecklistPanelContainer>
    );
} 